<template>
  <button
      class="btn"
      :disabled="disabled"
      :type="type"
      :class="[
        themes[theme],
        sizes[size],
        {
          'btn--design-new': design === 'new',
          'btn--beauty': disabled,
          'btn--preload': preload
        }
      ]"
  >
    <slot v-if="!preload" />
    <span v-if="preload && design === 'new'" style="visibility: hidden" aria-hidden="true"><slot /></span>
    <img v-if="preload" src="@/assets/img/preloader.svg" alt="" class="btn__preloader" />
  </button>
</template>

<script>
export default {
  name: 'AppButton',
  data() {
    return {
      themes: {
        default: 'btn--default',
        dark: 'btn--dark',
        error: 'btn--error',
        tooltip: 'btn--tooltip',
        warn: 'btn--warn',
        export: 'btn--export',
        empty: 'btn--empty',
        info: 'btn--blue',
        success: 'btn--success',
        outcome: 'btn--outcome',
        lavender: 'btn--lavender',
      },
      sizes: {
        normal: '',
        small: 'btn--small',
        table: 'btn--table'
      },
      preload: false,
    }
  },
  props: {
    design: {
      type: String,
      default: 'legacy',
      validator: value => ['legacy', 'new'].includes(value)
    },
    theme: {
      type: String,
      default: 'default'
    },
    size: {
      type: String,
      default: 'normal'
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    type: {
      type: String,
      default: 'button'
    },
  },
}
</script>

<style lang="sass">

.btn
  position: relative
  display: flex
  align-items: center
  justify-content: center
  gap: 10px
  min-width: 130px
  min-height: 38px
  padding: 12px 14px 11px
  border-radius: 8px
  color: #fff
  font-size: 15px
  font-weight: 500
  line-height: 1
  text-align: center
  text-decoration: none
  transition: background-color 0.3s
  cursor: pointer
  user-select: none

.btn--preload
  pointer-events: none

.btn--default
  background-color: $color-theme

  &:hover, &:focus
    background-color: #72a915

.btn--dark
  background-color: #939393

  &:hover
    background-color: #434343

.btn--error
  background-color: #dd4e4e

  &:hover
    background-color: #d23535

.btn--blue
  background-color: #35a4ea

  &:hover
    background-color: #439ad1

.btn--warn
  background-color: #f0c427

  &:hover
    background-color: #ebc027

.btn--outcome
  background-color: #e85c20

  &:hover
    background-color: #e54a07

.btn--lavender
  background-color: #a95ac5

  &:hover
    background-color: #a451c1

.btn--stretch
  width: 100%

.btn--center
  margin: 0 auto

.btn--beauty
  background-color: #c4c4c4
  pointer-events: none

.btn--small
  min-width: auto
  min-height: 100%
  padding: 9px 13px 8px
  font-size: 13px
  font-weight: 500

  &.btn--preload
    min-width: 70px
    min-height: 30px

.btn--tooltip
  min-width: auto
  min-height: auto
  width: 15px
  height: 15px
  padding: 0
  border-radius: 0
  background-image: url("~@/assets/img/tooltip-icon.svg")
  background-repeat: no-repeat
  background-position: right center

.btn__preloader
  position: absolute
  top: 50%
  left: 50%
  width: 20px
  height: 20px
  transform: translate(-50%, -50%)

.btn--export
  flex-shrink: 0
  position: relative
  min-width: auto
  min-height: auto
  padding: 0
  border-radius: 0
  color: #679c0d
  font-size: 14px
  font-weight: 600
  transition: opacity 0.3s
  opacity: 0.9

  &:before
    content: ""
    position: absolute
    right: -25px
    display: none
    width: 20px
    height: 20px
    background-image: url("~@/assets/img/preloader-export.svg")
    background-size: cover

  &:hover
    opacity: 1

.btn--export--loading
  pointer-events: none

  &:before
    display: block

.btn--empty
  min-width: auto
  min-height: auto
  padding: 0
  border-radius: 0

.btn--design-new
  box-sizing: border-box
  min-width: auto
  height: $ui-control-height
  min-height: $ui-control-height
  padding: 0 $ui-space-16
  border: 1px solid $ui-line-strong
  border-radius: $ui-control-radius
  background-color: $ui-surface
  color: $ui-ink
  font-family: $ui-font-body
  font-size: $ui-font-size-control
  font-weight: 500
  line-height: 1
  transition: background-color .15s ease

  &:hover:not(:disabled)
    background-color: $ui-surface-soft

  &:focus-visible
    outline: 2px solid var(--ui-accent, #{$ui-blue})
    outline-offset: 2px

  &:disabled,
  &.btn--beauty
    border-color: $ui-line
    background-color: $ui-surface-soft
    color: $ui-subtle
    cursor: not-allowed
    opacity: .7
    pointer-events: none

  &.btn--default,
  &.btn--blue
    border-color: var(--ui-accent, #{$ui-blue})
    background-color: var(--ui-accent, #{$ui-blue})
    color: #fff

    &:hover:not(:disabled),
    &:focus:not(:disabled)
      background-color: var(--ui-accent-hover, #{$ui-blue-hover})

  &.btn--dark
    border-color: $ui-line-strong
    background-color: $ui-surface-soft
    color: $ui-ink

    &:hover:not(:disabled)
      background-color: $ui-surface

  &.btn--error
    border-color: var(--ui-danger, #{$ui-red})
    background-color: var(--ui-danger, #{$ui-red})
    color: #fff

    &:hover:not(:disabled)
      background-color: var(--ui-danger-hover, #{$ui-red-hover})

  &.btn--success
    border-color: var(--ui-success, #{$ui-green})
    background-color: var(--ui-success, #{$ui-green})
    color: var(--ui-success-ink, #fff)

    &:hover:not(:disabled)
      background-color: var(--ui-success-hover, #{$ui-green-hover})

  &.btn--warn
    border-color: var(--ui-amber, #{$ui-amber})
    background-color: var(--ui-amber-soft, #{$ui-amber-soft})
    color: var(--ui-amber, #{$ui-amber})

    &:hover:not(:disabled)
      background-color: var(--ui-amber-soft, #{rgba($ui-amber, .16)})

  &.btn--empty,
  &.btn--export
    min-width: auto
    min-height: $ui-control-height-compact
    height: $ui-control-height-compact
    padding: 0 $ui-space-12
    border: 1px solid $ui-line-strong
    border-radius: $ui-control-radius
    background-color: $ui-surface-soft
    color: $ui-ink
    opacity: 1

    &:hover:not(:disabled)
      background-color: var(--ui-accent-soft, #{$ui-blue-soft})

  &.btn--small
    height: $ui-control-height-compact
    min-height: $ui-control-height-compact
    padding: 0 $ui-space-12
    font-size: $ui-font-size-control

  &.btn--table
    height: 30px
    min-height: 30px
    padding: 0 $ui-space-12
    white-space: nowrap
</style>
